import type { GuardianRequestResponse, GuardianRequestUserSummary } from "../../types/guardianTypes";
import { formatDate } from "../../utils/DateUtils";
import type { GuardianConnectionCardItem, GuardianRequestCardItem } from "./types";

export const formatGuardianUpdatedLabel = (updatedAt: string) =>
  `Updated ${formatDate(updatedAt)}`;

const formatInviteSentLabel = (updatedAt: string) => `Invite sent ${formatDate(updatedAt)}`;

export const getUserDisplayName = (user: GuardianRequestUserSummary | null, fallback: string) => {
  if (!user) {
    return fallback;
  }

  const fullName = `${user.firstName} ${user.lastName}`.trim();
  return fullName || fallback;
};

export const getUserEmail = (user: GuardianRequestUserSummary | null, fallback?: string | null) => user?.email ?? fallback ?? "No email provided";

export const dedupeById = <T extends { id: string }>(items: T[]) => {
  const byId = new Map<string, T>();
  items.forEach((item) => {
    byId.set(item.id, item);
  });
  return Array.from(byId.values());
};

export const sortByName = <T extends { name: string }>(items: T[]) =>
  [...items].sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));

export const mapActiveGuardians = (requests: GuardianRequestResponse[]): GuardianConnectionCardItem[] =>
  sortByName(
    dedupeById(
      requests
        .filter((request) => request.status === "ACTIVE" && request.guardianUser)
        .map((request) => ({
          id: request.guardianUserId ?? request.targetEmail ?? request.guardianUser?.id ?? "guardian",
          name: getUserDisplayName(request.guardianUser, "Guardian"),
          email: getUserEmail(request.guardianUser, request.targetEmail),
          statusLabel: request.status,
          meta: request.payoutGuardian ? "Payout guardian" : undefined,
        })),
    ),
  );

export const mapActiveTeens = (requests: GuardianRequestResponse[]): GuardianConnectionCardItem[] =>
  sortByName(
    dedupeById(
      requests
        .filter((request) => request.status === "ACTIVE" && request.teenUser)
        .map((request) => ({
          id: request.teenUserId ?? request.targetEmail ?? request.teenUser?.id ?? "teen",
          name: getUserDisplayName(request.teenUser, "Teen"),
          email: getUserEmail(request.teenUser, request.targetEmail),
          statusLabel: request.status,
        })),
    ),
  );

export const mapIncomingGuardianRequests = (requests: GuardianRequestResponse[]): GuardianRequestCardItem[] =>
  sortByName(
    requests
      .filter((request) => request.status === "PENDING")
      .map((request) => ({
        id: request.guardianUserId ?? request.targetEmail ?? "guardian-invite",
        name: request.pendingSignup ? request.targetEmail ?? "Guardian invite" : getUserDisplayName(request.guardianUser, "Guardian"),
        email: getUserEmail(request.guardianUser, request.targetEmail),
        statusLabel: request.status,
        updatedLabel: request.pendingSignup ? formatInviteSentLabel(request.updatedAt) : formatGuardianUpdatedLabel(request.updatedAt),
      })),
  );

export const mapOutgoingGuardianRequests = (requests: GuardianRequestResponse[]): GuardianRequestCardItem[] =>
  sortByName(
    requests
      .filter((request) => request.status === "PENDING")
      .map((request) => ({
        id: request.guardianUserId ?? request.targetEmail ?? "guardian-invite",
        name: request.pendingSignup ? request.targetEmail ?? "Guardian invite" : getUserDisplayName(request.guardianUser, "Guardian"),
        email: getUserEmail(request.guardianUser, request.targetEmail),
        statusLabel: request.status,
        updatedLabel: request.pendingSignup ? formatInviteSentLabel(request.updatedAt) : formatGuardianUpdatedLabel(request.updatedAt),
      })),
  );

export const mapIncomingTeenRequests = (requests: GuardianRequestResponse[]): GuardianRequestCardItem[] =>
  sortByName(
    requests
      .filter((request) => request.status === "PENDING")
      .map((request) => ({
        id: request.teenUserId ?? request.targetEmail ?? "teen-invite",
        name: request.pendingSignup ? request.targetEmail ?? "Teen invite" : getUserDisplayName(request.teenUser, "Teen"),
        email: getUserEmail(request.teenUser, request.targetEmail),
        statusLabel: request.status,
        updatedLabel: request.pendingSignup ? formatInviteSentLabel(request.updatedAt) : formatGuardianUpdatedLabel(request.updatedAt),
      })),
  );

export const mapOutgoingTeenRequests = (requests: GuardianRequestResponse[]): GuardianRequestCardItem[] =>
  sortByName(
    requests
      .filter((request) => request.status === "PENDING")
      .map((request) => ({
        id: request.teenUserId ?? request.targetEmail ?? "teen-invite",
        name: request.pendingSignup ? request.targetEmail ?? "Teen invite" : getUserDisplayName(request.teenUser, "Teen"),
        email: getUserEmail(request.teenUser, request.targetEmail),
        statusLabel: request.status,
        updatedLabel: request.pendingSignup ? formatInviteSentLabel(request.updatedAt) : formatGuardianUpdatedLabel(request.updatedAt),
      })),
  );
